import {
    Alert,
    Box,
    Button,
    Chip,
    Grid,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React from "react";
import {
    GuestCount,
    Reservation,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {
    Availability
} from "crqlar-react-common/dist/models/availability/availability.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {
    formatDateDMYHM,
    formatDateMDHM
} from "crqlar-react-common/dist/utils/date.util";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import {
    AgeCategory
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {
    ResourceCategory
} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import ReadOnlyField from "./ReadOnlyField";

interface Props {
    markedService: {
        reservations: Reservation[];
        startDate: Date;
        endDate: Date;
        service: Service;
        isException: boolean;
        availability: Availability;
    };
    tmpService: Service
    resources: Resource[];
    resourceCategories: ResourceCategory[];
    ageCategories: AgeCategory[];
    maxCapacity: number;
    localReservations: Reservation[]
}


const ServiceDialogPlannerDetails = ({
                                         markedService,
                                         tmpService,
                                         resources,
                                         resourceCategories,
                                         ageCategories,
                                         maxCapacity,
                                         localReservations
                                     }: Props) => {

    const {t, i18n} = useLocalize();
    // Filter out cancelled and no-show for display ONLY
    const reservationsForDisplay = localReservations.filter(
        r => r.status !== ReservationStatus.CANCELLED && r.status !== ReservationStatus.NO_SHOW
    );

    // Aggregate data for planner tab
    const guestCounts = (reservationsForDisplay || [])
        .flatMap(r => r.guestCounts || [])
        .filter(Boolean) as GuestCount[];

    const getCategoryLabel = (cat: AgeCategory) => {
        if (cat.name) return cat.name;
        const min = cat.minAge ?? 0;
        const max = cat.maxAge;
        if (max == null) return `${min}+`;
        return `${min}–${max}`;
    };

    const getCategoryCount = (cat: AgeCategory) => {
        return guestCounts
            .filter(gc => gc.ageCategoryId === cat.id)
            .reduce((sum, gc) => sum + (gc.count ?? 0), 0);
    };

    // Resources field - calculate chips
    const isSingleResource = resources.length === 1;
    const resourceChips = resources.map((resource) => {
        const categories = resourceCategories?.filter(c => resource.categoryIds?.includes(c.id!));
        const categoryNames = categories?.map(c => c.name).join(', ');
        const name = [resource.name || '', categoryNames].filter(Boolean).join(' - ');
        return (
            <a key={resource.id} href={`/system/resources/${resource.id}`} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                <Chip
                    label={name}
                    size="small"
                    deleteIcon={<OpenInNewIcon sx={{fontSize: 12, color: 'rgba(0,0,0,0.64) !important'}}/>}
                    onDelete={() => {}}
                    clickable
                />
            </a>
        );
    });

    // Availability field - calculate all derived data
    const availability = markedService.availability;
    const times = {
        start: markedService.startDate,
        end: markedService.endDate
    };
    const hasException = markedService.isException;
    const service = markedService.service;

    const resourceById = new Map(resources.map(r => [r.id!, r]));
    const availabilityTitle = resourceById.get(availability?.entityId ?? "")?.name ?? "";

    const r = availability?.rrule?.toUpperCase();
    const freq = r?.includes("WEEKLY") ? t("Weekly") : r?.includes("DAILY") ? t("Daily") : "";
    const capacity = availability?.capacityOverride !== undefined ? `(${availability.capacityOverride})` : "";

    const startDateTime = times?.start ? formatDateMDHM(times.start) : "";
    const endDateTime = times?.end ? formatDateMDHM(times.end) : "";
    const dateTimeRange = startDateTime && endDateTime ? ` | ${startDateTime} - ${endDateTime}` : "";

    const metaParts = [];
    if (freq) metaParts.push(freq);
    if (availability?.availabilityTypeClean?.toLowerCase() === "closed") {
        metaParts.push(t("Closed"));
    }
    const metaInfo = metaParts.length > 0 ? ` [${metaParts.join(", ")}]` : "";
    const availabilityName = `${availability?.name}${capacity}${dateTimeRange}${metaInfo}`;

    const isDisabled = availability?.availabilityTypeClean === "CLOSED" || hasException;

    const totalGuestCount = guestCounts.reduce((sum, gc) => sum + (gc.count ?? 0), 0);
    const effectiveCapacity = availability?.capacityOverride !== undefined
        ? Math.min(availability.capacityOverride, maxCapacity)
        : maxCapacity;

    const isFullyBooked = totalGuestCount >= effectiveCapacity;
    const isOverbooked = totalGuestCount > effectiveCapacity;

    let bookingAlertSeverity: 'info' | 'success' | 'error' = 'info';
    let bookingAlertMessage = '';

    if (isOverbooked) {
        bookingAlertSeverity = 'error';
        bookingAlertMessage = t("Capacity exceeded: {{guestCount}} guests have booked but maximal capacity is set to {{capacity}}", {
            guestCount: totalGuestCount,
            capacity: effectiveCapacity
        });
    } else if (isFullyBooked) {
        bookingAlertSeverity = 'success';
        bookingAlertMessage = t("This event is fully booked: {{guestCount}}/{{capacity}} guests", {
            guestCount: totalGuestCount,
            capacity: effectiveCapacity
        });
    } else {
        bookingAlertMessage = t("Current status: {{guestCount}}/{{capacity}} guests have booked this event", {
            guestCount: totalGuestCount,
            capacity: effectiveCapacity
        });
    }


    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <ReadOnlyField label={t('Start Date & Time')}
                               value={formatDateDMYHM(new Date(markedService.startDate))}
                               copyable/>
            </Grid>
            <Grid item xs={12} md={6}>
                <ReadOnlyField label={t('End Date & Time')}
                               value={formatDateDMYHM(new Date(markedService.endDate))}
                               copyable/>
            </Grid>
            <Grid item xs={12} md={6}>
                <ReadOnlyField label={t('Service')}
                               value={tmpService?.nameLocalized?.[i18n.language] || tmpService?.name || 'Service'}
                               copyable/>
            </Grid>
            <Grid item xs={12}>
                <Stack gap={1.5}>
                    <Typography variant="h6">
                        {t("Summary")}
                    </Typography>
                    {ageCategories.length > 0 ? (
                        <Stack direction="row" gap={2}>
                            {ageCategories.map(cat => (
                                <ReadOnlyField
                                    key={cat.id}
                                    flex="50%"
                                    label={t(getCategoryLabel(cat))}
                                    value={getCategoryCount(cat).toString()}
                                    copyable
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Stack direction="row" gap={2}>
                            <ReadOnlyField flex="50%" label={t('Adults')} value="0"
                                           copyable/>
                            <ReadOnlyField flex="50%" label={t('Children')}
                                           value="0" copyable/>
                        </Stack>
                    )}

                    <Alert severity={bookingAlertSeverity} variant="outlined">
                        {bookingAlertMessage}
                    </Alert>
                </Stack>
            </Grid>

            {resources && resources.length > 0 && (
                <Grid item xs={12}>
                    <Stack gap={1.5}>
                        <Typography variant="h6">
                            {t("Assigned Resources")}
                        </Typography>
                        <Box>
                            <TextField
                                fullWidth
                                label={t('Resources')}
                                value=" "
                                multiline={!isSingleResource}
                                InputProps={{
                                    readOnly: true,
                                    sx: {fontSize: '16px'},
                                    startAdornment: (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                flexWrap: 'wrap',
                                                width: '100%',
                                                gap: 0.5,
                                                pt: isSingleResource ? 0 : 1.2,
                                                pb: isSingleResource ? 0 : 1.2,
                                                pl: '0.5em',
                                                mt: isSingleResource ? "0.66rem" : undefined,
                                            }}
                                        >
                                            {resourceChips}
                                        </Box>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        alignItems: 'flex-start',
                                        paddingLeft: '0.5em',
                                        paddingTop: '0.3em',
                                        paddingBottom: '0.3em',
                                        minHeight: '56px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0,0,0,0.23) !important',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0,0,0,0.23) !important',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0,0,0,0.23) !important',
                                            borderWidth: "1px !important",
                                        },
                                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0,0,0,0.23) !important',
                                        },
                                        '&.Mui-active .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0,0,0,0.23) !important',
                                            borderWidth: "1px !important",
                                        },
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        fontSize: '16px',
                                        padding: 0,
                                        margin: 0,
                                        height: 0,
                                    },
                                }}
                            />
                        </Box>
                    </Stack>
                </Grid>
            )}

            {service?.availabilityBindings?.length && (
                <Grid item xs={12}>
                    <Stack gap={1.5}>
                        {isDisabled && (
                            <Alert severity="warning">
                                {t("This event is marked as closed")}
                            </Alert>
                        )}

                        <Typography variant="h6">
                            {t("Availabilities")}
                        </Typography>

                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <Typography sx={{minWidth: 140}}
                                        title={availabilityTitle}>
                                {availabilityTitle}
                            </Typography>

                            <Box sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5
                            }}>
                                <TextField
                                    value={availabilityName}
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <Box sx={{mr: '0.2em'}}>
                                                <CopyAdornment
                                                    value={availabilityName || ''}/>
                                            </Box>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            alignItems: 'center',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0,0,0,0.23) !important',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0,0,0,0.23) !important',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0,0,0,0.23) !important',
                                                borderWidth: "1px !important",
                                            },
                                            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0,0,0,0.23) !important',
                                            },
                                            '&.Mui-active .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0,0,0,0.23) !important',
                                                borderWidth: "1px !important",
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {},
                                    }}
                                />
                            </Box>

                            <Box>
                                <Button
                                    variant="outlined"
                                    component="a"
                                    href={`/system/availability/${availability?.entityId}?date=${markedService.startDate.toISOString().split('T')[0]}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    endIcon={<OpenInNewIcon/>}
                                    sx={{whiteSpace: "nowrap"}}
                                >
                                    {t("Edit")}
                                </Button>
                            </Box>
                        </Box>
                    </Stack>
                </Grid>
            )}
        </Grid>
    );
};

export default ServiceDialogPlannerDetails;